import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ArrowLeftRight, Plus, Search, ArrowUpCircle, ArrowDownCircle
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeLabel = { orden: 'Orden', muestra: 'Muestra', ajuste: 'Ajuste', aumento: 'Aumento' };
const typeColor = {
  orden: 'bg-blue-100 text-blue-700 border-blue-200',
  muestra: 'bg-purple-100 text-purple-700 border-purple-200',
  ajuste: 'bg-orange-100 text-orange-700 border-orange-200',
  aumento: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const AdminMovementsPage = () => {
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ product_id: '', movement_type: '', quantity: '', notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const [movRes, prodRes] = await Promise.all([
        axios.get(`${API}/operations/movements`),
        axios.get(`${API}/operations/products`),
      ]);
      setMovements(movRes.data);
      setProducts(prodRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);


  const openCreate = () => {
    setForm({ product_id: '', movement_type: '', quantity: '', notes: '' });
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!form.product_id || !form.movement_type || !form.quantity) { toast.error('Completa todos los campos requeridos'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/operations/movements`, {
        product_id: form.product_id,
        movement_type: form.movement_type,
        quantity: parseInt(form.quantity),
        notes: form.notes,
      });
      toast.success('Movimiento registrado');
      setShowCreate(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear movimiento'); }
    finally { setSaving(false); }
  };

  const filtered = movements.filter(m => {
    if (typeFilter !== 'all' && m.movement_type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!m.product_name.toLowerCase().includes(q) && !m.product_sku.toLowerCase().includes(q) && !(m.order_number || '').toLowerCase().includes(q) && !(m.notes || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Stats
  const totalIn = movements.filter(m => m.direction === 'in').reduce((s, m) => s + m.quantity, 0);
  const totalOut = movements.filter(m => m.direction === 'out').reduce((s, m) => s + m.quantity, 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-movements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Movimientos</h1>
          <p className="text-gray-500 mt-1">Registro de movimientos de inventario</p>
        </div>
        <Button onClick={openCreate} className="bg-[#467322] hover:bg-[#3A601C] text-white" data-testid="create-movement-btn">
          <Plus size={18} className="mr-2" /> Nuevo Movimiento
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-3 border-emerald-100"><div className="flex items-center gap-2"><ArrowUpCircle size={20} className="text-emerald-600" /><div><p className="text-xs text-gray-400">Entradas</p><p className="text-lg font-bold text-emerald-700">+{totalIn}</p></div></div></Card>
        <Card className="p-3 border-red-100"><div className="flex items-center gap-2"><ArrowDownCircle size={20} className="text-red-500" /><div><p className="text-xs text-gray-400">Salidas</p><p className="text-lg font-bold text-red-600">-{totalOut}</p></div></div></Card>
        <Card className="p-3 border-blue-100"><div className="flex items-center gap-2"><ArrowLeftRight size={20} className="text-blue-600" /><div><p className="text-xs text-gray-400">Total Movimientos</p><p className="text-lg font-bold text-gray-800">{movements.length}</p></div></div></Card>
        <Card className="p-3 border-amber-100"><div className="flex items-center gap-2"><ArrowDownCircle size={20} className="text-purple-600" /><div><p className="text-xs text-gray-400">Muestras</p><p className="text-lg font-bold text-purple-700">{movements.filter(m => m.movement_type === 'muestra').length}</p></div></div></Card>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <Input placeholder="Buscar por producto, SKU, orden..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-gray-50 border-gray-200 rounded-xl h-10" data-testid="search-movements" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40 bg-gray-50 border-gray-200 rounded-xl h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="orden">Órdenes</SelectItem>
            <SelectItem value="muestra">Muestras</SelectItem>
            <SelectItem value="ajuste">Ajustes</SelectItem>
            <SelectItem value="aumento">Aumentos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-gray-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead>Fecha</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Producto</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead className="text-center">Cantidad</TableHead>
              <TableHead>Referencia</TableHead>
              <TableHead>Notas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-gray-400">
                <ArrowLeftRight size={36} className="mx-auto mb-2 opacity-40" />
                <p>{search || typeFilter !== 'all' ? 'Sin resultados' : 'No hay movimientos registrados'}</p>
              </TableCell></TableRow>
            ) : filtered.map(m => (
              <TableRow key={m.id} className="hover:bg-gray-50" data-testid={`movement-row-${m.id}`}>
                <TableCell className="text-sm text-gray-500">{new Date(m.created_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                <TableCell><Badge className={`text-xs border ${typeColor[m.movement_type] || 'bg-gray-100 text-gray-700'}`}>{typeLabel[m.movement_type] || m.movement_type}</Badge></TableCell>
                <TableCell className="font-medium text-gray-800">{m.product_name}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-xs">{m.product_sku}</Badge></TableCell>
                <TableCell className="text-center">
                  <span className={`font-bold ${m.direction === 'in' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {m.direction === 'in' ? '+' : '-'}{m.quantity}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-gray-500">{m.order_number || '—'}</TableCell>
                <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{m.notes || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create Manual Movement Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Nuevo Movimiento Manual</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Producto *</Label>
              <Select value={form.product_id} onValueChange={v => setForm(f => ({ ...f, product_id: v }))}>
                <SelectTrigger className="bg-gray-50" data-testid="mov-select-product"><SelectValue placeholder="Seleccionar producto" /></SelectTrigger>
                <SelectContent>
                  {products.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name} (Disp: {p.quantity})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Tipo de Movimiento *</Label>
              <Select value={form.movement_type} onValueChange={v => setForm(f => ({ ...f, movement_type: v }))}>
                <SelectTrigger className="bg-gray-50" data-testid="mov-select-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="muestra">Muestra (Gasto publicitario)</SelectItem>
                  <SelectItem value="ajuste">Ajuste (Reducción manual)</SelectItem>
                  <SelectItem value="aumento">Aumento de Inventario</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Cantidad *</Label>
              <Input type="number" min="1" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} placeholder="0" data-testid="mov-quantity" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Notas</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Descripción del movimiento..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#467322] hover:bg-[#3A601C] text-white" data-testid="submit-movement-btn">
              {saving ? 'Registrando...' : 'Registrar Movimiento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMovementsPage;
